import { foldZones, type FoldedZones } from "../document/zones.ts";
import { COLLISION_CELLS_PER_TILE, type MapDocument, type PropInstance } from "../document/types.ts";
import { neighboursOf, type MapSide, type Neighbour } from "../document/layout.ts";
import type { DaedalusGameAdapter, ValidationIssue } from "./adapter.ts";
import type { BorderBandKind } from "./border-band.ts";
import { propBox } from "./issue-entries.ts";
import { crossingIssues } from "./seam-crossings.ts";
import { OPPOSITE, alongOrigin, edgeBox, painted, runWords, runsOf, type SeamParts } from "./seam-edge.ts";

export type { SeamParts };

/**
 * The seam checks (ADR-109 decision 6, P1.6-T145): for each edge of the map in view that has a placed neighbour,
 * warnings, never a refusal of a save or a compile. The terrain corners along the shared line that differ between
 * the two maps, listed as runs; a prop whose art overhangs the edge; each thing of a kind the game lists
 * (`DaedalusGameAdapter.borderBand`) within one chunk of the edge; and a zone type that differs across the edge, as
 * runs. A neighbour with no layers is blank: it has no ground, no zones and nothing to straddle, so an edge beside
 * it raises no warning at all; the warnings begin when the neighbour is painted. Everything here is pure and names nothing of any game: the kinds, the zone types and
 * their words are the adapter's.
 */

/**
 * The kind of a run of terrain corners that differ across an edge.
 */
export const SEAM_TERRAIN_KIND = "seam.terrain";

/**
 * The kind of a prop whose art overhangs an edge that has a neighbour.
 */
export const SEAM_PROP_KIND = "seam.prop";

/**
 * The kind of a run of tiles whose zone type differs across an edge.
 */
export const SEAM_ZONE_KIND = "seam.zone";

const zoneMemo = new WeakMap<MapDocument, FoldedZones>();

/**
 * A map's zones folded across its layers, kept for the map document.
 *
 * @param map The map.
 * @returns The folded zones.
 */
function foldedOf(map: MapDocument): FoldedZones {
  let folded = zoneMemo.get(map);
  if (folded === undefined) {
    folded = foldZones(map);
    zoneMemo.set(map, folded);
  }
  return folded;
}

/**
 * What the terrain layers of a map hold at one corner: each colour set, as terrain, variant and colour, sorted.
 *
 * @param map The map.
 * @param x The corner's column.
 * @param y The corner's row.
 * @returns The signature, empty when no terrain layer paints it.
 */
function cornerSignature(map: MapDocument, x: number, y: number): string {
  const found: string[] = [];
  const index = y * (map.width + 1) + x;
  for (const layer of map.layers) {
    if (layer.kind !== "terrain") {
      continue;
    }
    const value = layer.corners[index] ?? 0;
    if (value !== 0) {
      found.push(`${layer.terrain}/${layer.variant}/${value}`);
    }
  }
  return found.sort().join(";");
}

/**
 * The corner of a map along one of its sides.
 *
 * @param map The map.
 * @param side The side.
 * @param along The corner's number along the side.
 * @returns The signature of that corner.
 */
function edgeCorner(map: MapDocument, side: MapSide, along: number): string {
  switch (side) {
    case "north":
      return cornerSignature(map, along, 0);
    case "south":
      return cornerSignature(map, along, map.height);
    case "west":
      return cornerSignature(map, 0, along);
    default:
      return cornerSignature(map, map.width, along);
  }
}

/**
 * The warnings for the terrain corners that differ along one shared edge, one for each run.
 *
 * @param map The map in view.
 * @param other The neighbour.
 * @param edge Where the neighbour touches.
 * @param tileSize The tile size.
 * @returns The warnings.
 */
function terrainIssues(map: MapDocument, other: MapDocument, edge: Neighbour, tileSize: number): ValidationIssue[] {
  if (!painted(map)) {
    return [];
  }
  const shift = alongOrigin(map, edge.side) - alongOrigin(other, edge.side);
  const opposite = OPPOSITE[edge.side];
  const differing: number[] = [];
  for (let corner = edge.from; corner <= edge.to; corner += 1) {
    if (edgeCorner(map, edge.side, corner) !== edgeCorner(other, opposite, corner + shift)) {
      differing.push(corner);
    }
  }
  return runsOf(differing).map((run) => ({
    mapId: map.id,
    kind: SEAM_TERRAIN_KIND,
    severity: "warning",
    ignorable: true,
    box: edgeBox(map, edge.side, run[0], run[1], tileSize),
    message: `map ${map.id} has different terrain from ${other.name} along the ${edge.side} edge at ${runWords("corner", run)}; paint both sides of the line alike`,
  }));
}

/**
 * The zone type of one collision cell of a folded grid.
 *
 * @param folded The folded zones.
 * @param index The cell's index.
 * @param unpainted The type unpainted ground plays as, or null.
 * @returns The type id, null for none.
 */
function zoneAt(folded: FoldedZones, index: number, unpainted: string | null): string | null {
  const value = folded.cells[index] ?? 0;
  return value === 0 ? unpainted : (folded.types[value - 1] ?? unpainted);
}

/**
 * The index of the collision cell along a side of a map.
 *
 * @param map The map.
 * @param side The side.
 * @param along The cell's number along the side.
 * @returns The cell's index in the folded grid.
 */
function edgeCell(map: MapDocument, side: MapSide, along: number): number {
  const width = map.width * COLLISION_CELLS_PER_TILE;
  const height = map.height * COLLISION_CELLS_PER_TILE;
  switch (side) {
    case "north":
      return along;
    case "south":
      return (height - 1) * width + along;
    case "west":
      return along * width;
    default:
      return along * width + (width - 1);
  }
}

/**
 * The warnings for the zone types that differ across one shared edge, one for each run of tiles.
 *
 * @param map The map in view.
 * @param other The neighbour.
 * @param edge Where the neighbour touches.
 * @param adapter The adapter, whose zone types name the types and the unpainted one.
 * @param tileSize The tile size.
 * @returns The warnings.
 */
function zoneIssues(map: MapDocument, other: MapDocument, edge: Neighbour, adapter: DaedalusGameAdapter, tileSize: number): ValidationIssue[] {
  const types = adapter.zoneTypes ?? [];
  if (types.length === 0 || !painted(map)) {
    return [];
  }
  const unpainted = types.find((type) => type.unpainted === true)?.id ?? null;
  /**
   * The words for a zone type id.
   *
   * @param id The type id, or null for none.
   * @returns The type's label, its id when unlisted, or `no zone`.
   */
  const label = (id: string | null): string => (id === null ? "no zone" : (types.find((type) => type.id === id)?.label ?? id));
  const mine = foldedOf(map);
  const theirs = foldedOf(other);
  const shift = (alongOrigin(map, edge.side) - alongOrigin(other, edge.side)) * COLLISION_CELLS_PER_TILE;
  const opposite = OPPOSITE[edge.side];
  const kinds = new Map<number, readonly [string | null, string | null]>();
  const differing: number[] = [];
  for (let cell = edge.from * COLLISION_CELLS_PER_TILE; cell < edge.to * COLLISION_CELLS_PER_TILE; cell += 1) {
    const here = zoneAt(mine, edgeCell(map, edge.side, cell), unpainted);
    const there = zoneAt(theirs, edgeCell(other, opposite, cell + shift), unpainted);
    if (here !== there) {
      differing.push(cell);
      kinds.set(cell, [here, there]);
    }
  }
  const issues: ValidationIssue[] = [];
  let pending: number[] = [];
  /**
   * Closes the tiles gathered so far into one warning.
   *
   * @returns Nothing.
   */
  const flush = (): void => {
    if (pending.length === 0) {
      return;
    }
    const first = Math.floor((pending[0] as number) / COLLISION_CELLS_PER_TILE);
    const last = Math.floor((pending[pending.length - 1] as number) / COLLISION_CELLS_PER_TILE);
    const [here, there] = kinds.get(pending[0] as number) as readonly [string | null, string | null];
    issues.push({
      mapId: map.id,
      kind: SEAM_ZONE_KIND,
      severity: "warning",
      ignorable: true,
      box: edgeBox(map, edge.side, first, last + 1, tileSize),
      message: `map ${map.id} has the zone type ${label(here)} where ${other.name} has ${label(there)} along the ${edge.side} edge at ${runWords("tile", [first, last])}; use one zone type on both sides of the seam`,
    });
    pending = [];
  };
  let previous: readonly [string | null, string | null] | null = null;
  let lastCell = -2;
  for (const cell of differing) {
    const pair = kinds.get(cell) as readonly [string | null, string | null];
    const sameRun = previous !== null && cell - lastCell === 1 && pair[0] === previous[0] && pair[1] === previous[1];
    const sameTile = Math.floor(cell / COLLISION_CELLS_PER_TILE) === Math.floor(lastCell / COLLISION_CELLS_PER_TILE);
    if (!sameRun && !(sameTile && cell - lastCell === 1)) {
      flush();
    }
    pending.push(cell);
    previous = pair;
    lastCell = cell;
  }
  flush();
  return issues;
}

/**
 * Whether a tile position lies in the border band of an edge and along the stretch a neighbour covers.
 *
 * @param map The map.
 * @param edge The edge.
 * @param x The tile column.
 * @param y The tile row.
 * @param depth The band's depth in tiles.
 * @returns True when the position is in the band of that edge.
 */
function inBand(map: MapDocument, edge: Neighbour, x: number, y: number, depth: number): boolean {
  const horizontal = edge.side === "north" || edge.side === "south";
  const along = horizontal ? x : y;
  if (along < edge.from || along >= edge.to) {
    return false;
  }
  switch (edge.side) {
    case "north":
      return y < depth;
    case "south":
      return y >= map.height - depth;
    case "west":
      return x < depth;
    default:
      return x >= map.width - depth;
  }
}

/**
 * The first kind of the list a prop is of.
 *
 * @param prop The prop.
 * @param kinds The kinds.
 * @returns The kind, or undefined.
 */
function propKind(prop: PropInstance, kinds: readonly BorderBandKind[]): BorderBandKind | undefined {
  const behaviour = prop.behaviour;
  if (behaviour === undefined) {
    return undefined;
  }
  return kinds.find((kind) => kind.anyBehaviour === true || (kind.objectTypes ?? []).includes(behaviour.type));
}

/**
 * The warnings for the things of the game's kinds that stand within a chunk of an edge, and for the props whose
 * art overhangs it, one for each thing and edge.
 *
 * @param parts What the checks read.
 * @param other The neighbour.
 * @param edge Where it touches.
 * @returns The warnings.
 */
function thingIssues(parts: SeamParts, other: MapDocument, edge: Neighbour): ValidationIssue[] {
  const { map, tileSize, adapter } = parts;
  const depth = parts.world.chunkSize;
  const kinds = adapter.borderBand ?? [];
  const issues: ValidationIssue[] = [];
  for (const layer of map.layers) {
    if (layer.kind === "entity") {
      for (const entity of layer.entities) {
        const kind = kinds.find((candidate) => (candidate.entityTypes ?? []).includes(entity.type));
        if (kind !== undefined && inBand(map, edge, Math.floor(entity.x), Math.floor(entity.y), depth)) {
          issues.push({ mapId: map.id, layerId: layer.id, entityId: entity.id, kind: `seam.band-${kind.id}`, severity: "warning", ignorable: true, message: `marker ${entity.id} is a ${kind.label} within ${depth} tiles of the ${edge.side} edge, which ${other.name} shares; move it further in so a crowd or a fight never straddles two map servers` });
        }
      }
    } else if (layer.kind === "prop") {
      for (const prop of layer.props) {
        const kind = propKind(prop, kinds);
        if (kind !== undefined && inBand(map, edge, Math.floor(prop.x), Math.floor(prop.y), depth)) {
          issues.push({ mapId: map.id, layerId: layer.id, propId: prop.id, kind: `seam.band-${kind.id}`, severity: "warning", ignorable: true, message: `prop ${prop.id} is a ${kind.label} within ${depth} tiles of the ${edge.side} edge, which ${other.name} shares; move it further in so a crowd or a fight never straddles two map servers` });
        }
        const box = propBox(prop, parts.sizeOf, tileSize);
        const horizontal = edge.side === "north" || edge.side === "south";
        const from = (horizontal ? box.x : box.y) / tileSize;
        const to = (horizontal ? box.x + box.w : box.y + box.h) / tileSize;
        const overhangs = edge.side === "north" ? box.y < 0 : edge.side === "south" ? box.y + box.h > map.height * tileSize : edge.side === "west" ? box.x < 0 : box.x + box.w > map.width * tileSize;
        if (overhangs && to > edge.from && from < edge.to) {
          issues.push({ mapId: map.id, layerId: layer.id, propId: prop.id, kind: SEAM_PROP_KIND, severity: "warning", ignorable: true, message: `prop ${prop.id} has art that reaches past the ${edge.side} edge into ${other.name}; move it back so no prop crosses the edge` });
        }
      }
    }
  }
  return issues;
}

/**
 * The seam checks of one map: for each placed neighbour that is not blank, in the order `neighboursOf` gives them, the terrain
 * runs, the zone runs, then the things.
 *
 * @param parts What the checks read.
 * @returns The warnings; none for a map with no place or no neighbour.
 */
export function seamIssues(parts: SeamParts): readonly ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  for (const edge of neighboursOf(parts.world, parts.map.id)) {
    const other = parts.world.maps.find((candidate) => candidate.id === edge.map);
    if (other === undefined || !painted(other)) {
      continue;
    }
    issues.push(...terrainIssues(parts.map, other, edge, parts.tileSize), ...zoneIssues(parts.map, other, edge, parts.adapter, parts.tileSize), ...thingIssues(parts, other, edge));
  }
  issues.push(...crossingIssues(parts));
  return issues;
}
